// Client for sm4rt_sync_rs2dv (RepairShopr -> Dataverse sync). Calls go to sm4rt-api's
// staff-authenticated passthrough (/api/rs-sync/* -> rs2dv /api/*), which adds the
// function key server-side. Dev: the CRA '/api' proxy sends this to sm4rt-api on
// :7071, which forwards to rs2dv on :7073. Only allow-listed routes are forwarded —
// a new endpoint here needs an entry in sm4rt-api's api/rs_sync_proxy too.
const API_BASE_URL = '/api/rs-sync';

export const SYNC_ENTITIES = [
  'users',
  'customers',
  'products',
  'tickets',
  'invoices',
  'schedules',
  'invoice_schedule_items',
  'payment_methods',
  'payments',
] as const;

export type SyncEntity = typeof SYNC_ENTITIES[number];

export interface SyncResult {
  entity: string;
  updated: number;
  errors: number;
  processName: string;
  sourceRecordCount: number | null;
  targetRecordCount: number | null;
  sourceTotalRecordCount: number | null;
  targetTotalRecordCount: number | null;
  updatedRecordCount: number | null;
  failedRecordCount: number;
  summaryInfo: string;
  summaryAlert: string;
  alertSeverity: string;
  elapsed_seconds: number;
}

export interface SyncResponse {
  results: SyncResult[];
  elapsed_seconds: number;
  rs_api_calls: number;
  dv_api_calls: number;
}

export interface ReconcileResult {
  entity: string;
  rs_total: number;
  dv_total: number;
  diff: number;
  status: string;
}

export interface ReconcileResponse {
  results: ReconcileResult[];
  elapsed_seconds: number;
  rs_api_calls: number;
  dv_api_calls: number;
}

export interface FullSyncParams {
  from?: string;
  dryRun?: boolean;
  updateLimit?: number;
}

export interface ResyncParams {
  from?: string;
  to?: string;
  dryRun?: boolean;
  enrich?: boolean;
  updateLimit?: number;
  fromPage?: number;
  id?: string;
}

function buildParams(obj: Record<string, string | number | boolean | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`Request failed (${response.status}): ${text}`);
  }
  return response.json();
}

export async function runFullSync(
  params: FullSyncParams,
  signal?: AbortSignal
): Promise<SyncResponse> {
  const qs = buildParams({
    from: params.from,
    dry_run: params.dryRun,
    update_limit: params.updateLimit,
  });

  const response = await fetch(`${API_BASE_URL}/sync${qs}`, {
    method: 'POST',
    signal,
  });

  return handleResponse<SyncResponse>(response);
}

export async function runEntityResync(
  entity: string,
  params: ResyncParams,
  signal?: AbortSignal
): Promise<SyncResponse> {
  const qs = buildParams({
    from: params.from,
    to: params.to,
    dry_run: params.dryRun,
    enrich: params.enrich,
    update_limit: params.updateLimit,
    from_page: params.fromPage,
    id: params.id,
  });

  const response = await fetch(
    `${API_BASE_URL}/resync/${encodeURIComponent(entity)}${qs}`,
    { method: 'POST', signal }
  );

  // resync returns a single result, normalize to SyncResponse shape
  const data = await handleResponse<SyncResponse | SyncResult>(response);
  if ('results' in data) return data;
  return { results: [data], elapsed_seconds: data.elapsed_seconds, rs_api_calls: 0, dv_api_calls: 0 };
}

export async function runReconcile(
  signal?: AbortSignal
): Promise<ReconcileResponse> {
  const response = await fetch(`${API_BASE_URL}/reconcile`, { signal });
  return handleResponse<ReconcileResponse>(response);
}
